/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-05-25 22:25:50
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Card, ButtonGroup, Button, Badge } from '@shopify/polaris';
import { EditMajor, DeleteMajor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import NoMetafields from 'src/components/views/tools/metafield/no-metafields';
import LoadingRows from 'src/components/views/tools/metafield/loading-rows';

import ConfirmDeleteModal from 'src/components/views/tools/metafield/confirm-delete-modal';

type Props = {
  list: Array<Object>,
  error: Array<Object>,
  loading: boolean,
  deleteMetafield: () => void,
  deleting: boolean,
  success: boolean,
  handleActiveModalEdit: () => void
};

type ComposedProps = Props & WithI18nProps;

type State = {
  activeModalRemove: boolean,
  activeModalEdit: boolean,
  index: number,
  selectedId: number
};

class ListMetafield extends Component<ComposedProps, State> {
  state = {
    activeModalRemove: false,
    activeModalEdit: false,
    index: -1,
    selectedId: -1
  };

  componentWillReceiveProps(nextProps) {
    const { deleting, success } = nextProps;
    const { deleting: deletingPrev } = this.props;

    if (success === true && deleting !== deletingPrev) {
      this.handleActiveOptionModalRemove();
      this.setState({ selectedId: '' });
    }
  }

  render() {
    const { i18n, list, loading, error } = this.props;

    return (
      <>
        <Card.Section>
          <div className="Polaris-DataTable">
            <div className="Polaris-DataTable__ScrollContainer" style={{ marginLeft: 0 }}>
              <table className="Polaris-DataTable__Table">
                <thead>
                  <tr>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.Metafield.Metafield.namespace')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.Metafield.Metafield.key')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.Metafield.Metafield.appliesTo')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--numeric">
                      {i18n.translate('AdminHTML.Metafield.Metafield.actions')}
                    </th>
                  </tr>
                </thead>
                {!_.isEmpty(list) ? (
                  <tbody>
                    {list.map((item, key) => {
                      // Render error badge
                      const badge =
                        _.findIndex(error, { namespace: item.namespace, key: item.key }) !== -1 ? (
                          <Badge progress="incomplete" status="warning">
                            {i18n.translate('AdminHTML.Metafield.Metafield.error')}
                          </Badge>
                        ) : (
                          ''
                        );

                      return (
                        // Enable drag for per option row
                        <tr className="Polaris-DataTable__TableRow" key={`metafield-${key}`}>
                          {/* Namespace */}
                          <td className="Polaris-DataTable__Cell">
                            {item.namespace} {badge}
                          </td>

                          {/* Key */}
                          <td className="Polaris-DataTable__Cell">{item.key}</td>

                          {/* Apply for */}
                          <td className="Polaris-DataTable__Cell">
                            {this.renderApplyToFeatures(item.id, item.appliesTo)}
                          </td>

                          {/* Actions */}
                          <td className="Polaris-DataTable__Cell Polaris-DataTable__Cell-Action">
                            {this.renderAction(key)}
                          </td>
                        </tr>
                      );
                    })}
                    {loading && (
                      // Loading rows while reading metafields
                      <LoadingRows />
                    )}
                  </tbody>
                ) : (
                  <tbody>
                    {loading && (
                      // Loading rows while reading metafields
                      <LoadingRows />
                    )}
                  </tbody>
                )}
              </table>

              {/* No metafields card */}
              {_.isEmpty(list) && !loading ? (
                <NoMetafields handleChange={this.handleActiveModalEdit} />
              ) : (
                ''
              )}
            </div>
          </div>
        </Card.Section>

        {/* Confirm delete modal */}
        <ConfirmDeleteModal
          active={this.state.activeModalRemove}
          handleChange={this.handleActiveOptionModalRemove}
          handleRemove={this.deleteMetafield}
          deleting={this.props.deleting}
          title={i18n.translate('AdminHTML.Metafield.Metafield.confirmDelete_Title')}
          content={i18n.translate('AdminHTML.Metafield.Metafield.confirmDelete_Content')}
        />
      </>
    );
  }

  renderApplyToFeatures = (ruleId, appliesTo) => {
    const { i18n } = this.props;
    const labels = {
      search: i18n.translate('AdminHTML.Metafield.Metafield.appliesTo_Search'),
      filterOption: i18n.translate('AdminHTML.Metafield.Metafield.appliesTo_FilterOption'),
      display: i18n.translate('AdminHTML.Metafield.Metafield.appliesTo_Display'),
      sortByOption: i18n.translate('AdminHTML.Metafield.Metafield.appliesTo_SortByOption')
    };

    return _.isObject(appliesTo) ? (
      <div>
        <ul className="appliesTo">
          {Object.keys(appliesTo).map(key => {
            return appliesTo[key] ? (
              <li key={key}>
                <span>{labels[key]}</span>
              </li>
            ) : (
              ''
            );
          })}
        </ul>
      </div>
    ) : (
      ''
    );
  };

  // Render action button for per filter
  renderAction = (index: number) => {
    return (
      <ButtonGroup segmented>
        <Button
          size="slim"
          icon={EditMajor}
          onClick={() => {
            this.handleActiveModalEdit(index);
          }}
        />
        <Button
          size="slim"
          icon={DeleteMajor}
          onClick={() => {
            this.updateSelected(index);
            this.handleActiveOptionModalRemove(index);
          }}
        />
      </ButtonGroup>
    );
  };

  // Show/Hide Edit modal
  handleActiveModalEdit = index => {
    this.props.handleActiveModalEdit(index);
  };

  // Update selected id
  updateSelected = index => {
    this.setState(({ selectedId }) => ({
      selectedId: index
    }));
  };

  // Show/Hide Remove modal
  handleActiveOptionModalRemove = () => {
    this.setState(({ activeModalRemove }) => ({
      activeModalRemove: !activeModalRemove
    }));
  };

  /**
   * Delete rule
   */
  deleteMetafield = () => {
    const { selectedId } = this.state;
    const { list } = this.props;

    // Get metafield based on selected id
    const metafield = list[selectedId] || {};

    this.props.deleteMetafield(metafield);
  };
}

export default withI18n()(ListMetafield);
